import { Alert, Button, Heading, Modal, ModalFooter, Row, Tooltip } from '@amzn/meridian';
import { ErrorAlert } from 'components/ErrorAlert';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { isEmpty, values } from 'es-toolkit/compat';
import { useVueAppGetter } from 'features/detail-page/campaigns/campaigns.util';
import type { AllProjectPodsGetter, SymphonyPod } from 'features/preview/preview-session.types';
import { CampaignConfigProvider } from 'features/traffic/campaigns/config/CampaignConfigProvider';
import { useGetTpAssetMapQuery } from 'generated/graphql';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useState } from 'react';
import { type LandingPagePreviewType, LandingPagePreviewTypes, type PreviewCampaignsErrors, PreviewTypes } from './advanced-preview.types';
import { getContainerIdBySelectPageTypeInLandingPagePreview } from './advanced-preview-campaigns.util';
import { AdvancedPreviewCampaignsError } from './components/AdvancedPreviewCampaignsError';
import { AdvancedPreviewCampaignsTable } from './components/AdvancedPreviewCampaignsTable';
import { AdvancedPreviewGurupaAlert } from './components/AdvancedPreviewGurupaAlert';
import { AdvancedPreviewLoader } from './components/AdvancedPreviewLoader';
import { AdvancedPreviewModalInfo } from './components/AdvancedPreviewModalInfo';
import { AdvancedPreviewPerformanceWarning } from './components/AdvancedPreviewPerformanceWarning';
import { AdvancedPreviewResult } from './components/AdvancedPreviewResult';
import { AdvancedPreviewSettingsSection } from './components/AdvancedPreviewSettingsSection';
import { useStartPreviewSession } from './hooks/useStartPreviewSession';
import { useLandingPagePreviewCampaigns } from './useLandingPagePreviewCampaigns';

type LandingPreviewModalProps = {
  isLandingPagePreview: boolean;
  isLoading: boolean;
  trafficProjectId: string;
  onCloseModal: () => void;
  openModal: boolean;
};

const LandingPagePreviewModal = ({
  onCloseModal,
  openModal,
  isLandingPagePreview,
  trafficProjectId,
  isLoading,
}: LandingPreviewModalProps) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>('');
  const [selectedCategoryNode, setSelectedCategoryNode] = useState<string>('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedPageType, setSelectedPageType] = useState<LandingPagePreviewType>(LandingPagePreviewTypes.LANDING_PAGE_TYPE);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [previewSessionDisabled, setPreviewSessionDisabled] = useState<boolean>(true);
  const [previewCampaignsErrors, setPreviewCampaignsErrors] = useState<PreviewCampaignsErrors[]>([]);

  const { trafficCampaignsTableData, trafficDisplayCampaigns, trafficCampaignsLoading, trafficCampaignsError } =
    useLandingPagePreviewCampaigns({
      openModal,
      trafficProjectId,
    });

  const settingsIncomplete =
    isEmpty(selectedCustomerId) ||
    isEmpty(selectedLanguages) ||
    isEmpty(selectedPlatforms) ||
    (selectedPageType === LandingPagePreviewTypes.LANDING_PAGE_TYPE && isEmpty(selectedCategoryNode));

  useEffect(() => {
    const shouldDisablePreviewSession = settingsIncomplete || isEmpty(selectedRowKeys);

    setPreviewSessionDisabled(shouldDisablePreviewSession);
  }, [
    settingsIncomplete,
    selectedRowKeys,
  ]);

  const { alias } = useUserData();

  const podsModules = useVueAppGetter<AllProjectPodsGetter>('pods/getAllProjectPods') ?? {};
  const campaigns: SymphonyPod[] = values(podsModules).map((podModule) => podModule.podData);
  const { refetch: refetchAssetMapQuery } = useGetTpAssetMapQuery({
    skip: true,
  });

  const containerId = getContainerIdBySelectPageTypeInLandingPagePreview(selectedPageType, selectedCategoryNode);

  const { startPreviewSession, progressPercent, progressMessage, previewUrl, previewLoading, previewError } = useStartPreviewSession({
    campaigns,
    marketplace: selectedMarketplace,
    podsModules,
    previewPageType: PreviewTypes.LANDING_PAGE,
    refetchAssetMapQuery,
    selectedContainerId: containerId,
    selectedCustomerId,
    selectedLanguages,
    selectedPageType,
    selectedRowKeys,
    setPreviewCampaignsErrors,
    trafficDisplayCampaigns,
    username: alias ?? '',
  });

  return (
    <CampaignConfigProvider>
      <Modal
        aria-describedby="landing-page-preview-modal"
        open={openModal}
        scrollContainer="viewport"
        title="Landing Page Preview"
        width="75rem"
      >
        {!trafficCampaignsLoading && trafficCampaignsError && (
          <Row
            alignmentHorizontal="start"
            spacingInset="300"
            widths={[
              'fill',
            ]}
          >
            <ErrorAlert contextMessage="Unable to load campaigns for preview" error={trafficCampaignsError} />
          </Row>
        )}
        {previewCampaignsErrors.length > 0 && !isEmpty(previewUrl) && (
          <AdvancedPreviewCampaignsError
            previewCampaignsErrors={previewCampaignsErrors}
            setPreviewCampaignsErrors={setPreviewCampaignsErrors}
          />
        )}
        <AdvancedPreviewModalInfo marketplace={selectedMarketplace} />
        <AdvancedPreviewSettingsSection
          campaigns={trafficCampaignsTableData}
          isLandingPagePreview={isLandingPagePreview}
          selectedCategoryNode={selectedCategoryNode}
          selectedCustomerId={selectedCustomerId}
          selectedLanguages={selectedLanguages}
          selectedMarketplace={selectedMarketplace}
          selectedPageType={selectedPageType}
          selectedPlatforms={selectedPlatforms}
          setSelectedCategoryNode={setSelectedCategoryNode}
          setSelectedCustomerId={setSelectedCustomerId}
          setSelectedLanguages={setSelectedLanguages}
          setSelectedMarketplace={setSelectedMarketplace}
          setSelectedPageType={setSelectedPageType}
          setSelectedPlatforms={setSelectedPlatforms}
        />
        <Row spacingInset="300">
          <Heading level={6}>Which campaigns would you like to preview?</Heading>
        </Row>
        <Row spacingInset="300">
          <Alert size="small" type="neutral">
            Campaigns must have placements scheduled to detail page slots with valid content to be available for preview.
          </Alert>
        </Row>
        <AdvancedPreviewCampaignsTable
          campaigns={trafficCampaignsTableData}
          isLoading={trafficCampaignsLoading}
          selectedCategoryNode={selectedCategoryNode}
          selectedLanguages={selectedLanguages}
          selectedPageType={selectedPageType}
          selectedPlatforms={selectedPlatforms}
          selectedRowKeys={selectedRowKeys}
          selectionDisabled={settingsIncomplete}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <AdvancedPreviewPerformanceWarning selectedCount={selectedRowKeys.length} selectedLanguagesCount={selectedLanguages.length} />
        <AdvancedPreviewGurupaAlert />
        <LoadingOverlay dataCy="landing-page-preview-loader" visible={trafficCampaignsLoading || isLoading} />
        <AdvancedPreviewLoader percentage={progressPercent} progressMessage={progressMessage} visible={previewLoading} />
        <AdvancedPreviewResult failedPreviewSessionError={previewError} previewUrl={previewUrl} />
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button disabled={previewLoading} onClick={onCloseModal} type="secondary">
              Cancel
            </Button>
            <Button
              data-cy="landing-page-start-preview-session-button"
              disabled={previewSessionDisabled || previewLoading}
              onClick={startPreviewSession}
              type={!isEmpty(previewUrl) ? 'secondary' : 'primary'}
            >
              {previewSessionDisabled ? (
                <Tooltip position="top" title="Preview Error">
                  <span>Start Preview Session</span>
                </Tooltip>
                // biome-ignore lint/style/noNestedTernary: legacy code
              ) : !isEmpty(previewUrl) ? (
                'Restart preview session'
              ) : (
                'Start preview session'
              )}
            </Button>
            {previewUrl && (
              <Button data-cy="landing-page-preview-url-button" href={previewUrl} target="_blank" type="primary">
                Open preview
              </Button>
            )}
          </Row>
        </ModalFooter>
      </Modal>
    </CampaignConfigProvider>
  );
};

export { LandingPagePreviewModal };
